package fr.frux.shared.data.local

import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.result.runCatchingCancellable

/**
 * Variante des cibles sans SQLite : le web garde la pyramide en localStorage. Le quota y est borne
 * et le navigateur peut refuser l'ecriture, d'ou des echecs avales et journalises. La copie n'est
 * qu'un accelerateur, le reseau reste la source.
 */
class PreferencesClusterPyramidLocalDataSource(
    private val preferences: AppPreferences,
) : ClusterPyramidLocalDataSource {
    override suspend fun read(): CachedClusterPyramid? =
        guarded("lecture") {
            val payload = preferences.getString(KEY_PAYLOAD) ?: return@guarded null
            CachedClusterPyramid(
                etag = preferences.getString(KEY_ETAG),
                payload = payload,
                checkedAtMillis = preferences.getString(KEY_CHECKED_AT)?.toLongOrNull() ?: 0L,
            )
        }

    override suspend fun write(
        etag: String?,
        payload: String,
        checkedAtMillis: Long,
    ) {
        val ok =
            guarded("ecriture") {
                // Le payload d'abord : s'il depasse le quota, l'ancienne etag ne doit pas survivre
                // a cote d'un payload qui n'a pas ete remplace.
                preferences.putString(KEY_PAYLOAD, payload)
                if (etag != null) preferences.putString(KEY_ETAG, etag) else preferences.remove(KEY_ETAG)
                preferences.putString(KEY_CHECKED_AT, checkedAtMillis.toString())
                true
            }
        if (ok == null) guarded("purge") { preferences.remove(KEY_PAYLOAD) }
    }

    override suspend fun touch(checkedAtMillis: Long) {
        guarded("touch") {
            if (preferences.getString(KEY_PAYLOAD) != null) {
                preferences.putString(KEY_CHECKED_AT, checkedAtMillis.toString())
            }
        }
    }

    private suspend fun <T> guarded(
        what: String,
        block: suspend () -> T?,
    ): T? =
        runCatchingCancellable { block() }
            .onFailure { Logger.e(TAG, "Cache local de la pyramide inaccessible ($what)", it) }
            .getOrNull()

    companion object {
        private const val TAG = "ClusterPyramidLocalDataSource"
        const val KEY_PAYLOAD = "cluster_pyramid_payload"
        const val KEY_ETAG = "cluster_pyramid_etag"
        const val KEY_CHECKED_AT = "cluster_pyramid_checked_at"
    }
}
